import React, { useState, useEffect, useRef } from "react";
import ReactQuill, { Quill } from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import ImageResize from "quill-image-resize-module-react";
import {
  Upload,
  Image as ImageIcon,
  Save,
  Send,
  Calendar,
  Type,
  Trash2,
  ChevronLeft,
  ChevronRight,
  Plus,
  Loader2,
  FileText,
  Sparkles,
  Tag,
  Code2,
  UserCheck,
  Lightbulb,
} from "lucide-react";
import styles from "./Projects.module.css";

const ColorAttributor = Quill.import("attributors/style/color");
const BackgroundAttributor = Quill.import("attributors/style/background");
const SizeAttributor = Quill.import("attributors/style/size");

const fontSizeWhitelist = [
  "4px",
  "6px",
  "8px",
  "9px",
  "10px",
  "11px",
  "12px",
  "13px",
  "14px",
  "15px",
  "16px",
  "18px",
  "20px",
  "22px",
  "24px",
  "26px",
  "28px",
  "32px",
];
SizeAttributor.whitelist = fontSizeWhitelist;

Quill.register(ColorAttributor, true);
Quill.register(BackgroundAttributor, true);
Quill.register(SizeAttributor, true);
Quill.register("modules/imageResize", ImageResize);

export default function CreateProject({ onNewProject, draftData }) {
  const [title, setTitle] = useState(draftData?.title || "");
  const [type, setType] = useState(
    draftData?.type && ["App", "Data", "AI"].includes(draftData.type)
      ? draftData.type
      : draftData?.type
      ? "Custom"
      : "App"
  );
  const [customType, setCustomType] = useState(
    draftData?.type && !["App", "Data", "AI"].includes(draftData.type)
      ? draftData.type
      : ""
  );
  const [isCustomType, setIsCustomType] = useState(
    draftData?.type ? !["App", "Data", "AI"].includes(draftData.type) : false
  );
  const [description, setDescription] = useState(draftData?.description || "");
  const [keySkills, setKeySkills] = useState(draftData?.keySkills || "");
  const [overview, setOverview] = useState(draftData?.overview || "");
  const [myContribution, setMyContribution] = useState(draftData?.myContribution || "");
  const [challengesSolutions, setChallengesSolutions] = useState(draftData?.challengesSolutions || "");

  const [date, setDate] = useState(draftData?.date || "");
  const [coverImage, setCoverImage] = useState(draftData?.coverImage || "");
  const [images, setImages] = useState(draftData?.images || []);
  const [status, setStatus] = useState("");
  const [uploading, setUploading] = useState(false);
  const [coverUploading, setCoverUploading] = useState(false);

  const fileInputRef = useRef(null);
  const coverFileInputRef = useRef(null);
  const API_BASE = process.env.REACT_APP_API_BASE || (typeof window !== "undefined" && window.location?.hostname ? `http://${window.location.hostname}:3001` : "http://localhost:3001");

  useEffect(() => {
    if (draftData) {
      setTitle(draftData.title || "");
      const pType = draftData.type || "App";
      if (["App", "Data", "AI"].includes(pType)) {
        setType(pType);
        setIsCustomType(false);
        setCustomType("");
      } else {
        setType("Custom");
        setIsCustomType(true);
        setCustomType(pType);
      }
      const rawDesc = draftData.description || "";
      setDescription(rawDesc);
      setKeySkills(draftData.keySkills || "");
      
      // Overview fallback if draft has legacy description
      const hasNewFields = draftData.overview || draftData.keySkills || draftData.myContribution || draftData.challengesSolutions;
      setOverview(draftData.overview || (!hasNewFields && rawDesc ? rawDesc : ""));
      setMyContribution(draftData.myContribution || "");
      setChallengesSolutions(draftData.challengesSolutions || "");
      
      setDate(draftData.date ? draftData.date.split("T")[0] : "");
      setCoverImage(draftData.coverImage || "");

      // Extract existing images or fallback to embedded <img> tags
      if (Array.isArray(draftData.images) && draftData.images.length > 0) {
        setImages(draftData.images);
      } else if (draftData.description) {
        const matches = [
          ...draftData.description.matchAll(/<img[^>]+src="([^">]+)"/g),
        ];
        const extracted = matches.map((m) => m[1]);
        if (extracted.length > 0) {
          setImages(extracted);
        }
      }
    }
  }, [draftData]);

  const modules = {
    toolbar: [
      [{ header: [1, 2, 3, false] }],
      [{ size: fontSizeWhitelist }],
      ["bold", "italic", "underline", "blockquote"],
      [{ color: [] }, { background: [] }],
      [{ list: "ordered" }, { list: "bullet" }],
      ["link", "clean"],
    ],
  };

  const formats = [
    "header",
    "size",
    "bold",
    "italic",
    "underline",
    "blockquote",
    "list",
    "bullet",
    "link",
    "color",
    "background",
  ];

  // Upload Cover Image file to /api/upload
  const handleCoverUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setCoverUploading(true);
    setStatus("Uploading cover image...");

    try {
      const formData = new FormData();
      formData.append("image", file);
      formData.append("type", "project");
      formData.append("title", (title.trim() || "untitled") + "-cover");

      const res = await fetch(`${API_BASE}/api/upload`, {
        method: "POST",
        body: formData,
      });

      const data = await res.json();
      if (data.url) {
        setCoverImage(data.url);
        setStatus("✅ Cover image uploaded successfully!");
      } else {
        setStatus("❌ Failed to upload cover image");
      }
    } catch (err) {
      console.error("Cover upload failed:", err);
      setStatus("❌ Cover upload failed: " + err.message);
    } finally {
      setCoverUploading(false);
      if (coverFileInputRef.current) coverFileInputRef.current.value = "";
      setTimeout(() => setStatus(""), 4000);
    }
  };

  // Upload gallery image files to /api/upload
  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    setUploading(true);
    setStatus("Uploading gallery images...");

    try {
      const uploadedUrls = [];
      for (const file of files) {
        const formData = new FormData();
        formData.append("image", file);
        formData.append("type", "project");
        formData.append("title", title.trim() || "untitled");

        const res = await fetch(`${API_BASE}/api/upload`, {
          method: "POST",
          body: formData,
        });

        const data = await res.json();
        if (data.url) {
          uploadedUrls.push(data.url);
        }
      }

      if (uploadedUrls.length > 0) {
        setImages((prev) => [...prev, ...uploadedUrls]);
        setStatus(`✅ ${uploadedUrls.length} image(s) uploaded successfully!`);
      } else {
        setStatus("❌ Failed to upload images");
      }
    } catch (err) {
      console.error("Upload failed:", err);
      setStatus("❌ Upload failed: " + err.message);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
      setTimeout(() => setStatus(""), 4000);
    }
  };

  // Reorder / Delete gallery image
  const handleMoveImage = (idx, direction) => {
    setImages((prev) => {
      const next = [...prev];
      const targetIdx = idx + direction;
      if (targetIdx < 0 || targetIdx >= next.length) return prev;
      const temp = next[idx];
      next[idx] = next[targetIdx];
      next[targetIdx] = temp;
      return next;
    });
  };

  const handleDeleteImage = (idx) => {
    setImages((prev) => prev.filter((_, i) => i !== idx));
  };

  // Save Draft (localStorage)
  const handleSaveDraft = () => {
    const finalType = isCustomType ? customType.trim() || "App" : type;
    const newDraft = {
      id: draftData?.id || `draft-${Date.now()}`,
      title: title.trim() || "Untitled Project Draft",
      type: finalType,
      description: overview || description,
      keySkills,
      overview,
      myContribution,
      challengesSolutions,
      date,
      coverImage,
      images,
      savedAt: new Date().toISOString(),
    };

    const existing = JSON.parse(localStorage.getItem("project-drafts") || "[]");
    const updated = [
      ...existing.filter((d) => d.id !== newDraft.id),
      newDraft,
    ];

    localStorage.setItem("project-drafts", JSON.stringify(updated));
    setStatus("✅ Draft saved to local curator storage!");
    setTimeout(() => setStatus(""), 4000);
  };

  // Submit Project (Backend API)
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      let url = `${API_BASE}/api/projects`;
      let method = "POST";

      if (draftData?._id) {
        url = `${API_BASE}/api/projects/${draftData._id}`;
        method = "PUT";
      }

      const finalType = isCustomType ? customType.trim() || "App" : type;

      const res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          title,
          type: finalType,
          description: overview || description,
          keySkills,
          overview,
          myContribution,
          challengesSolutions,
          date,
          coverImage,
          images,
        }),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to save project");

      if (method === "PUT") {
        setStatus("✅ Exhibition project updated successfully!");
      } else {
        setStatus("✅ Exhibition project published successfully!");
      }

      onNewProject?.(data);

      // Clean up draft if submitting from draft
      if (draftData?.id) {
        const existing = JSON.parse(
          localStorage.getItem("project-drafts") || "[]"
        );
        const updated = existing.filter((d) => d.id !== draftData.id);
        localStorage.setItem("project-drafts", JSON.stringify(updated));
      }

      setTitle("");
      setType("App");
      setCustomType("");
      setIsCustomType(false);
      setDescription("");
      setKeySkills("");
      setOverview("");
      setMyContribution("");
      setChallengesSolutions("");
      setDate("");
      setCoverImage("");
      setImages([]);
      setTimeout(() => setStatus(""), 5000);
    } catch (err) {
      alert(err.message);
    }
  };

  return (
    <div className="w-full max-w-6xl mx-auto p-1 text-xs">
      <form onSubmit={handleSubmit} className="flex flex-col gap-3.5">
        {/* Title, Type & Date Row */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-2.5 bg-white dark:bg-gray-900 p-3.5 rounded-xl border border-gray-200/70 dark:border-gray-800 shadow-sm">
          <div className="md:col-span-2">
            <label className="block text-[11px] font-bold uppercase tracking-wider text-gray-500 mb-1 flex items-center gap-1">
              <Type size={13} className="text-[var(--primary)]" />
              Project Title
            </label>
            <input
              type="text"
              placeholder="e.g. Elim Bread of Life Church App"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full p-2 text-xs rounded-lg border border-gray-200 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100 focus:outline-none focus:border-[var(--primary)] transition"
              required
            />
          </div>

          <div>
            <label className="block text-[11px] font-bold uppercase tracking-wider text-gray-500 mb-1 flex items-center gap-1">
              <Tag size={13} className="text-[var(--primary)]" />
              Project Type
            </label>
            <div className="flex flex-col gap-1">
              <select
                value={isCustomType ? "Custom" : type}
                onChange={(e) => {
                  if (e.target.value === "Custom") {
                    setIsCustomType(true);
                    setType("Custom");
                  } else {
                    setIsCustomType(false);
                    setType(e.target.value);
                  }
                }}
                className="w-full p-2 text-xs rounded-lg border border-gray-200 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100 focus:outline-none focus:border-[var(--primary)] transition font-medium"
              >
                <option value="App">App</option>
                <option value="Data">Data</option>
                <option value="AI">AI</option>
                <option value="Custom">Custom (自定义)...</option>
              </select>
              {isCustomType && (
                <input
                  type="text"
                  placeholder="Enter custom type..."
                  value={customType}
                  onChange={(e) => setCustomType(e.target.value)}
                  className="w-full p-1.5 text-xs rounded-lg border border-green-500 dark:border-green-600 dark:bg-gray-800 dark:text-gray-100 focus:outline-none focus:border-[var(--primary)] transition"
                  required
                />
              )}
            </div>
          </div>

          <div>
            <label className="block text-[11px] font-bold uppercase tracking-wider text-gray-500 mb-1 flex items-center gap-1">
              <Calendar size={13} className="text-[var(--primary)]" />
              Exhibition Date
            </label>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="w-full p-2 text-xs rounded-lg border border-gray-200 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100 focus:outline-none focus:border-[var(--primary)] transition"
            />
          </div>
        </div>

        {/* 1. KEY SKILLS */}
        <div className="bg-white dark:bg-gray-900 p-3.5 rounded-xl border border-gray-200/70 dark:border-gray-800 shadow-sm flex flex-col gap-1.5">
          <div className="flex items-center justify-between">
            <label className="text-[11px] font-bold uppercase tracking-wider text-gray-700 dark:text-gray-200 flex items-center gap-1.5">
              <Code2 size={13} className="text-[var(--primary)]" />
              1. Key Skills (主要技术与技能)
            </label>
            <span className="text-[10px] text-gray-400 italic">
              Technologies, frameworks, tools, platforms
            </span>
          </div>
          <div className={styles.resizableEditorContainer}>
            <ReactQuill
              value={keySkills}
              onChange={setKeySkills}
              modules={modules}
              formats={formats}
              theme="snow"
              placeholder="e.g. Next.js, React, TypeScript, Tailwind CSS, Framer Motion, Node.js, Express.js, MongoDB, Mongoose, Cloudinary, JWT, REST API..."
            />
          </div>
          <div className={styles.editorResizeHint}>
            <span>Supports simple multiline text, lists, or bullets</span>
            <span className={styles.editorResizeBadge}>↕ Drag bottom corner to resize</span>
          </div>
        </div>

        {/* 2. OVERVIEW */}
        <div className="bg-white dark:bg-gray-900 p-3.5 rounded-xl border border-gray-200/70 dark:border-gray-800 shadow-sm flex flex-col gap-1.5">
          <div className="flex items-center justify-between">
            <label className="text-[11px] font-bold uppercase tracking-wider text-gray-700 dark:text-gray-200 flex items-center gap-1.5">
              <FileText size={13} className="text-[var(--primary)]" />
              2. Overview (项目概述)
            </label>
            <span className="text-[10px] text-gray-400 italic">
              What the project is, why it was created, and what problem it solves
            </span>
          </div>
          <div className={styles.resizableEditorContainer}>
            <ReactQuill
              value={overview}
              onChange={setOverview}
              modules={modules}
              formats={formats}
              theme="snow"
              placeholder="e.g. Elim Bread of Life Church App is a full-featured church web platform designed to connect members with the community..."
            />
          </div>
          <div className={styles.editorResizeHint}>
            <span>Supports multiple paragraphs and rich text formatting</span>
            <span className={styles.editorResizeBadge}>↕ Drag bottom corner to resize</span>
          </div>
        </div>

        {/* 3. MY CONTRIBUTION */}
        <div className="bg-white dark:bg-gray-900 p-3.5 rounded-xl border border-gray-200/70 dark:border-gray-800 shadow-sm flex flex-col gap-1.5">
          <div className="flex items-center justify-between">
            <label className="text-[11px] font-bold uppercase tracking-wider text-gray-700 dark:text-gray-200 flex items-center gap-1.5">
              <UserCheck size={13} className="text-[var(--primary)]" />
              3. My Contribution (个人职责与贡献)
            </label>
            <span className="text-[10px] text-gray-400 italic">
              What you personally designed, developed, implemented, or maintained
            </span>
          </div>
          <div className={styles.resizableEditorContainer}>
            <ReactQuill
              value={myContribution}
              onChange={setMyContribution}
              modules={modules}
              formats={formats}
              theme="snow"
              placeholder="e.g. - Designed and developed the application architecture.&#10;- Built responsive frontend using Next.js, React and TypeScript.&#10;- Developed REST APIs using Node.js and Express..."
            />
          </div>
          <div className={styles.editorResizeHint}>
            <span>Highlight personal achievements and core responsibilities</span>
            <span className={styles.editorResizeBadge}>↕ Drag bottom corner to resize</span>
          </div>
        </div>

        {/* 4. CHALLENGES & SOLUTIONS */}
        <div className="bg-white dark:bg-gray-900 p-3.5 rounded-xl border border-gray-200/70 dark:border-gray-800 shadow-sm flex flex-col gap-1.5">
          <div className="flex items-center justify-between">
            <label className="text-[11px] font-bold uppercase tracking-wider text-gray-700 dark:text-gray-200 flex items-center gap-1.5">
              <Lightbulb size={13} className="text-[var(--primary)]" />
              4. Challenges & Solutions (挑战与解决方案)
            </label>
            <span className="text-[10px] text-gray-400 italic">
              Demonstrate problem solving abilities and technical depth
            </span>
          </div>
          <div className={styles.resizableEditorContainer}>
            <ReactQuill
              value={challengesSolutions}
              onChange={setChallengesSolutions}
              modules={modules}
              formats={formats}
              theme="snow"
              placeholder="Challenge: The application needed to efficiently handle high-res images without affecting load times.&#10;Solution: Integrated Cloudinary for storage, transformations, and implemented optimized lazy loading..."
            />
          </div>
          <div className={styles.editorResizeHint}>
            <span>Write multiple Challenge / Solution pairs</span>
            <span className={styles.editorResizeBadge}>↕ Drag bottom corner to resize</span>
          </div>
        </div>

        {/* Cover Image Upload Section (紧凑并排布局，无长条) */}
        <div className="bg-white dark:bg-gray-900 p-3.5 rounded-xl border border-gray-200/70 dark:border-gray-800 shadow-sm flex flex-col gap-2.5">
          <div className="flex items-center justify-between">
            <label className="text-[11px] font-bold uppercase tracking-wider text-gray-500 flex items-center gap-1.5">
              <Sparkles size={13} className="text-[var(--primary)]" />
              Cover Image (封面图片)
            </label>
            <span className="text-[10px] text-gray-400 italic">
              Main artwork cover for the exhibition gallery card
            </span>
          </div>

          <div className="flex items-center gap-3">
            {/* Compact Upload Box */}
            <div
              onClick={() => coverFileInputRef.current?.click()}
              className="w-48 h-28 border-2 border-dashed border-green-600/30 hover:border-[var(--primary)] rounded-xl bg-green-50/20 dark:bg-gray-800/40 flex flex-col items-center justify-center p-2 text-center cursor-pointer transition hover:bg-green-50/50 flex-shrink-0"
            >
              <input
                ref={coverFileInputRef}
                type="file"
                accept="image/*"
                onChange={handleCoverUpload}
                className="hidden"
              />
              {coverUploading ? (
                <Loader2 size={20} className="animate-spin text-[var(--primary)] mb-1" />
              ) : (
                <Upload size={20} className="text-[var(--primary)] mb-1" />
              )}
              <span className="font-semibold text-xs text-gray-700 dark:text-gray-200">
                {coverUploading ? "Uploading..." : "Upload Cover"}
              </span>
              <span className="text-[10px] text-gray-400 mt-0.5">JPG, PNG, WebP</span>
            </div>

            {/* Cover Preview */}
            {coverImage ? (
              <div className="relative w-48 h-28 rounded-xl overflow-hidden border border-green-400 bg-gray-50 flex-shrink-0 shadow-sm group">
                <img
                  src={coverImage}
                  alt="Cover Preview"
                  className="w-full h-full object-cover"
                />
                <span className="absolute top-1.5 left-1.5 bg-[var(--primary)] text-white text-[9px] font-bold px-1.5 py-0.5 rounded shadow">
                  Cover
                </span>
                <button
                  type="button"
                  onClick={() => setCoverImage("")}
                  className="absolute top-1.5 right-1.5 bg-red-600/85 hover:bg-red-600 text-white p-1 rounded-md transition opacity-0 group-hover:opacity-100 shadow"
                  title="Remove Cover"
                >
                  <Trash2 size={12} />
                </button>
              </div>
            ) : (
              <div className="w-48 h-28 rounded-xl border border-dashed border-gray-200 dark:border-gray-800 flex flex-col items-center justify-center text-gray-400 flex-shrink-0 bg-gray-50/40 dark:bg-gray-800/20 text-[10px]">
                <ImageIcon size={22} className="opacity-35 mb-1" />
                <span>No cover uploaded</span>
              </div>
            )}
          </div>
        </div>

        {/* Dedicated Gallery Images Upload Section (6列网格 + 首位上传插槽) */}
        <div className="bg-white dark:bg-gray-900 p-3.5 rounded-xl border border-gray-200/70 dark:border-gray-800 shadow-sm flex flex-col gap-2.5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <label className="text-[11px] font-bold uppercase tracking-wider text-gray-500 flex items-center gap-1.5">
                <ImageIcon size={13} className="text-[var(--primary)]" />
                Gallery Images ({images.length} attached)
              </label>
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="px-2.5 py-0.5 text-[11px] font-bold bg-green-100 dark:bg-green-950/60 text-green-700 dark:text-green-300 rounded-md hover:bg-green-200 transition flex items-center gap-1"
              >
                {uploading ? (
                  <Loader2 size={11} className="animate-spin" />
                ) : (
                  <Plus size={11} />
                )}
                <span>{uploading ? "Uploading..." : "Upload Artworks"}</span>
              </button>
            </div>

            <span className="text-[10px] text-gray-400 italic">
              Displays neatly in 6-column gallery on detail page
            </span>
          </div>

          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="image/*"
            onChange={handleFileUpload}
            className="hidden"
          />

          {/* 6-Per-Row Grid with Interactive First Upload Slot */}
          <div className={styles.adminPreviewGrid}>
            {/* Interactive Upload Tile Slot */}
            <div
              onClick={() => fileInputRef.current?.click()}
              className={styles.adminUploadTile}
              title="Click to select image files"
            >
              {uploading ? (
                <Loader2 size={18} className="animate-spin text-[var(--primary)]" />
              ) : (
                <Plus size={20} className="text-[var(--primary)]" />
              )}
              <span className="text-[10px] font-semibold text-gray-600 dark:text-gray-300">
                {uploading ? "Uploading..." : "Add Images"}
              </span>
            </div>

            {/* Uploaded Gallery Thumbnails */}
            {images.map((imgUrl, idx) => (
              <div key={idx} className={styles.adminThumbItem}>
                <img
                  src={imgUrl}
                  alt={`Preview ${idx + 1}`}
                  className={styles.adminThumbImg}
                />

                {/* Badge */}
                <span className="absolute top-1 left-1 bg-black/60 backdrop-blur-sm text-white text-[9px] font-bold px-1 rounded">
                  #{idx + 1}
                </span>

                {/* Hover Actions: Move left, right, delete */}
                <div className={styles.adminThumbActions}>
                  {idx > 0 && (
                    <button
                      type="button"
                      onClick={() => handleMoveImage(idx, -1)}
                      className={styles.adminThumbBtn}
                      title="Move left"
                    >
                      <ChevronLeft size={12} />
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => handleDeleteImage(idx)}
                    className={`${styles.adminThumbBtn} ${styles.adminThumbBtnDanger}`}
                    title="Delete image"
                  >
                    <Trash2 size={12} />
                  </button>
                  {idx < images.length - 1 && (
                    <button
                      type="button"
                      onClick={() => handleMoveImage(idx, 1)}
                      className={styles.adminThumbBtn}
                      title="Move right"
                    >
                      <ChevronRight size={12} />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Action Controls */}
        <div className="flex items-center justify-between pt-1">
          <div className="flex gap-2">
            <button
              type="submit"
              disabled={uploading || coverUploading}
              className="px-4 py-1.5 text-xs font-bold bg-[var(--primary)] text-white rounded-lg hover:opacity-90 transition flex items-center gap-1.5 shadow-sm disabled:opacity-50"
            >
              <Send size={13} />
              {draftData?._id ? "Update Project" : "Publish Project"}
            </button>

            <button
              type="button"
              onClick={handleSaveDraft}
              className="px-3.5 py-1.5 text-xs font-semibold bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 transition flex items-center gap-1.5"
            >
              <Save size={13} />
              Save Draft
            </button>
          </div>

          {status && (
            <span
              className={`text-xs font-semibold ${status.startsWith("✅")
                  ? "text-green-600 dark:text-green-400"
                  : "text-red-500"
                }`}
            >
              {status}
            </span>
          )}
        </div>
      </form>
    </div>
  );
}
